import { Component, lazy, Suspense, type ErrorInfo, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { UnreadProvider } from './context/UnreadContext';
import { PlatformAuthProvider } from './context/PlatformAuthContext';
import { useAuth } from './context/AuthContext';
import { AuthGuard } from './components/AuthGuard';
import { PlatformGuard } from './components/PlatformGuard';
import { AdminGuard, AdminOrSupervisorGuard } from './components/AdminGuard';
import { Layout } from './components/Layout';
import { PlatformLayout } from './components/PlatformLayout';
import { INBOX_PATH } from './lib/routeConfig';
import Login from './pages/Login';
import Inbox from './pages/Inbox';

const Analytics = lazy(() => import('./pages/Analytics'));
const CustomerAnalyticsDetail = lazy(() => import('./pages/CustomerAnalyticsDetail'));
const AgentAnalyticsDetail = lazy(() => import('./pages/AgentAnalyticsDetail'));
const Settings = lazy(() => import('./pages/Settings'));
const Knowledge = lazy(() => import('./pages/Knowledge'));
const PausedCustomers = lazy(() => import('./pages/PausedCustomers'));
const Team = lazy(() => import('./pages/Team'));
const CannedReplies = lazy(() => import('./pages/CannedReplies'));
const Departments = lazy(() => import('./pages/Departments'));
const Branches = lazy(() => import('./pages/Branches'));
const Integrations = lazy(() => import('./pages/Integrations'));
const Resolutions = lazy(() => import('./pages/Resolutions'));
const Suggestions = lazy(() => import('./pages/Suggestions'));
const Templates = lazy(() => import('./pages/Templates'));
const Campaigns = lazy(() => import('./pages/Campaigns'));
const BranchLinks = lazy(() => import('./pages/BranchLinks'));
const Billing = lazy(() => import('./pages/Billing'));
const SuperAdminLogin = lazy(() => import('./pages/SuperAdminLogin'));
const SuperAdminTenants = lazy(() => import('./pages/SuperAdminTenants'));
const SuperAdminAudit = lazy(() => import('./pages/SuperAdminAudit'));
const SuperAdminBilling = lazy(() => import('./pages/SuperAdminBilling'));
const SuperAdminPaymentIncidents = lazy(() => import('./pages/SuperAdminPaymentIncidents'));
const SuperAdminPaymentIncidentDetail = lazy(
  () => import('./pages/SuperAdminPaymentIncidentDetail'),
);

export function AnalyticsRouteFallback() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="flex min-h-48 items-center justify-center text-body-medium text-on-surface-variant"
    >
      {t('loading')}
    </div>
  );
}

interface RouteRecoveryBoundaryProps {
  children: ReactNode;
  onReload?: () => void;
}

interface RouteRecoveryBoundaryState {
  failed: boolean;
}

function RouteRecoveryFallback({ onReload }: { onReload: () => void }) {
  const { t } = useTranslation();
  return (
    <main
      role="alert"
      className="flex min-h-[70vh] items-center justify-center px-6 py-12 text-center"
    >
      <div className="max-w-md">
        <h1 className="text-title-large font-semibold text-on-surface">
          {t('chunk_load_title')}
        </h1>
        <p className="mt-3 text-body-medium leading-6 text-on-surface-variant">
          {t('chunk_load_error')}
        </p>
        <button
          type="button"
          onClick={onReload}
          className="mt-6 min-h-11 rounded-full bg-primary px-6 py-2.5 text-label-large font-semibold text-on-primary transition-colors hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          {t('reload_app')}
        </button>
      </div>
    </main>
  );
}

export class RouteRecoveryBoundary extends Component<
  RouteRecoveryBoundaryProps,
  RouteRecoveryBoundaryState
> {
  state: RouteRecoveryBoundaryState = { failed: false };

  static getDerivedStateFromError(): RouteRecoveryBoundaryState {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep the operator-facing state free of raw chunk URLs or stack traces,
    // while retaining a diagnostic in the browser console.
    // eslint-disable-next-line no-console -- Error-boundary diagnostics are intentionally browser-local.
    console.error('Route rendering failed', error, info);
  }

  render() {
    if (this.state.failed) {
      return (
        <RouteRecoveryFallback
          onReload={this.props.onReload ?? (() => window.location.reload())}
        />
      );
    }
    return this.props.children;
  }
}

function lazyRoute(element: React.ReactNode) {
  return <Suspense fallback={<AnalyticsRouteFallback />}>{element}</Suspense>;
}

// Tenant plane: AuthProvider is scoped to ONLY these routes (not global) so
// the tenant /api/auth/me check never runs while on a /super_admin/* route.
function TenantShell() {
  return (
    <AuthProvider>
      <UnreadProvider>
        <Outlet />
      </UnreadProvider>
    </AuthProvider>
  );
}

// Platform plane: a fully separate provider, mounted ONLY under /super_admin/*.
// A tenant-agent page never mounts this and never calls
// /api/super_admin/auth/me — the isolation boundary holds at the routing
// level too, not just inside the guard.
function PlatformShell() {
  return (
    <PlatformAuthProvider>
      <Outlet />
    </PlatformAuthProvider>
  );
}

export function TeamRoute() {
  const { agent, loading } = useAuth();
  if (loading) return null;
  if (agent?.role === 'supervisor') return <Navigate to="/analytics?view=team" replace />;
  if (agent?.role !== 'admin') return <Navigate to={INBOX_PATH} replace />;
  return lazyRoute(<Team />);
}

export function LegacyConversationRedirect() {
  const { phone } = useParams();
  const params = new URLSearchParams({ status: 'bot_replying' });
  if (phone) params.set('conversation', phone);
  return <Navigate to={`${INBOX_PATH}?${params.toString()}`} replace />;
}

export function LegacyConversationsRedirect() {
  return <Navigate to={`${INBOX_PATH}?status=bot_replying`} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <RouteRecoveryBoundary>
          <Routes>
          {/* ── Platform super-admin plane — isolated route tree ────────────── */}
          <Route element={<PlatformShell />}>
            <Route path="/super_admin/login" element={lazyRoute(<SuperAdminLogin />)} />
            <Route
              path="/super_admin"
              element={
                <PlatformGuard>
                  <PlatformLayout />
                </PlatformGuard>
              }
            >
              <Route index element={lazyRoute(<SuperAdminTenants />)} />
              <Route path="billing" element={lazyRoute(<SuperAdminBilling />)} />
              {/* Incident review lives under billing but is its own surface: the
                  list is the work queue, the :incidentId route is a deep link an
                  operator can be sent straight to. */}
              <Route
                path="billing/incidents"
                element={lazyRoute(<SuperAdminPaymentIncidents />)}
              />
              <Route
                path="billing/incidents/:incidentId"
                element={lazyRoute(<SuperAdminPaymentIncidentDetail />)}
              />
              <Route path="audit" element={lazyRoute(<SuperAdminAudit />)} />
              {/* Unknown platform sub-paths stay within the platform area. */}
              <Route path="*" element={<Navigate to="/super_admin" replace />} />
            </Route>
          </Route>

          {/* ── Tenant plane — unchanged behavior, unchanged routes ─────────── */}
          <Route element={<TenantShell />}>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <AuthGuard>
                  <Layout />
                </AuthGuard>
              }
            >
              {/* Full-bleed routes are listed in src/lib/routeConfig.ts (read by Layout). */}
              <Route index element={<Navigate to={INBOX_PATH} replace />} />
              <Route path="inbox" element={<Inbox />} />
              {/* Master-detail: the ticket detail renders inside the Inbox workspace. */}
              <Route path="tickets/:id" element={<Inbox />} />
              <Route
                path="settings"
                element={<AdminGuard>{lazyRoute(<Settings />)}</AdminGuard>}
              />
              <Route
                path="billing"
                element={<AdminGuard>{lazyRoute(<Billing />)}</AdminGuard>}
              />
              <Route path="conversations" element={<LegacyConversationsRedirect />} />
              <Route path="conversations/:phone" element={<LegacyConversationRedirect />} />
              <Route path="paused" element={lazyRoute(<PausedCustomers />)} />
              {/* Open to every role on purpose — no AdminGuard. */}
              <Route path="suggestions" element={lazyRoute(<Suggestions />)} />
              <Route path="analytics" element={lazyRoute(<Analytics />)} />
              <Route
                path="analytics/customers/:phone"
                element={lazyRoute(<CustomerAnalyticsDetail />)}
              />
              <Route
                path="analytics/agents/:agentId"
                element={lazyRoute(<AgentAnalyticsDetail />)}
              />
              <Route
                path="team"
                element={<TeamRoute />}
              />
              <Route
                path="canned"
                element={
                  <AdminGuard>
                    {lazyRoute(<CannedReplies />)}
                  </AdminGuard>
                }
              />
              <Route
                path="knowledge"
                element={
                  <AdminGuard>
                    {lazyRoute(<Knowledge />)}
                  </AdminGuard>
                }
              />
              <Route
                path="departments"
                element={
                  <AdminGuard>
                    {lazyRoute(<Departments />)}
                  </AdminGuard>
                }
              />
              <Route
                path="branches"
                element={
                  <AdminGuard>
                    {lazyRoute(<Branches />)}
                  </AdminGuard>
                }
              />
              <Route
                path="integrations"
                element={
                  <AdminGuard>
                    {lazyRoute(<Integrations />)}
                  </AdminGuard>
                }
              />
              <Route
                path="templates"
                element={
                  <AdminGuard>
                    {lazyRoute(<Templates />)}
                  </AdminGuard>
                }
              />
              <Route
                path="campaigns"
                element={
                  <AdminGuard>
                    {lazyRoute(<Campaigns />)}
                  </AdminGuard>
                }
              />
              <Route
                path="links"
                element={
                  <AdminGuard>
                    {lazyRoute(<BranchLinks />)}
                  </AdminGuard>
                }
              />
              <Route
                path="resolutions"
                element={
                  <AdminOrSupervisorGuard>
                    {lazyRoute(<Resolutions />)}
                  </AdminOrSupervisorGuard>
                }
              />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to={INBOX_PATH} replace />} />
          </Routes>
        </RouteRecoveryBoundary>
      </ThemeProvider>
    </BrowserRouter>
  );
}
